import type {
  AuthResponse,
  ManagedUser,
  Permission,
  UserRole,
} from '../types/domain.js'
import { http } from './http.js'

export const authApi = {
  login: (username: string, password: string) =>
    http.post<AuthResponse>('/auth/login', { username, password }),
  me: () => http.get<AuthResponse>('/auth/me'),
  logout: () => http.post<void>('/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    http.post<{ status: string }>('/auth/password/change', { currentPassword, newPassword }),
  startEnrollment: () =>
    http.post<{ secret: string; otpauthUri: string }>('/auth/mfa/enrollment'),
  confirmEnrollment: (code: string) =>
    http.post<{ status: string; recoveryCodes: string[] }>('/auth/mfa/enrollment/confirm', { code }),
  verifyMfa: (value: { code?: string; recoveryCode?: string; trustDevice?: boolean }) =>
    http.post<{ status: string }>('/auth/mfa/verify', value),
  forgetTrustedDevices: () =>
    http.post<{ count: number }>('/auth/trusted-devices/forget'),
  regenerateRecoveryCodes: (password: string, code: string) =>
    http.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes/regenerate', { password, code }),
  updateProfile: (name: string, email: string) =>
    http.patch<{ id: string; username: string; name: string; email: string; role: UserRole }>('/auth/profile', { name, email }),
}

export interface UserMutation {
  username?: string
  email?: string
  name?: string
  role?: UserRole
  active?: boolean
  channelIds?: string[]
  permissionOverrides?: Partial<Record<Permission, boolean>>
  password?: string
  generatePassword?: boolean
}

export const usersApi = {
  list: (query = '') => http.get<ManagedUser[]>(`/users${query}`),
  create: (input: UserMutation) => http.post<{ user: ManagedUser; temporaryPassword: string }>('/users', input),
  update: (id: string, input: UserMutation) => http.patch<ManagedUser>(`/users/${id}`, input),
  remove: (id: string) => http.delete<void>(`/users/${id}`),
  resetPassword: (id: string, password?: string) =>
    http.post<{ temporaryPassword: string }>(`/users/${id}/password-reset`, { password }),
  resetMfa: (id: string) => http.post<void>(`/users/${id}/mfa-reset`),
  revokeSessions: (id: string) => http.post<{ count: number }>(`/users/${id}/sessions/revoke`),
  audit: (query = '') => http.get<{
    items: SecurityAuditEvent[]
    total: number
    page: number
    pageSize: number
  }>(`/security-audit${query}`),
}

export interface SecurityAuditEvent {
  id: string
  action: string
  outcome: string
  attemptedUsername: string | null
  ip: string | null
  userAgent: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  actor: { id: string; username: string; name: string } | null
  target: { id: string; username: string; name: string } | null
}

